import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../components/common/Button';
import Toast from '../../components/common/Toast';
import { Competition } from '../../types/interfaces';
import { CompetitionPermissions, errorMessage, saveCompetitionFields } from '../../services/manageApi';
import ReadOnlyField from './ReadOnlyField';

type Leaderboard = 'public' | 'private' | 'unlisted' | 'final';

interface Props {
  competition: Competition;
  permissions: CompetitionPermissions;
  canEdit: (field: string) => boolean;
  onSaved: (competition: Competition) => void;
  onDirtyChange: (section: string, dirty: boolean) => void;
}

const SECTION = 'evaluation-settings';
const FIELDS = ['number_of_eval_rounds', 'leaderboard', 'feedback_required'];

function initialValues(competition: Competition) {
  return {
    rounds: String(competition.number_of_eval_rounds ?? 1),
    leaderboard: ((competition.leaderboard as Leaderboard) || 'public') as Leaderboard,
    feedbackRequired: Boolean(competition.feedback_required),
  };
}

const EvaluationSettingsForm: React.FC<Props> = ({ competition, permissions, canEdit, onSaved, onDirtyChange }) => {
  const initial = useMemo(() => initialValues(competition), [competition]);
  const [values, setValues] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => {
    setValues(initial);
  }, [initial]);

  const dirty = values.rounds !== initial.rounds
    || values.leaderboard !== initial.leaderboard
    || values.feedbackRequired !== initial.feedbackRequired;

  useEffect(() => {
    onDirtyChange(SECTION, dirty);
  }, [dirty, onDirtyChange]);

  const editable = FIELDS.every(canEdit);

  if (!editable) {
    return (
      <section className="manage-section" data-tour="manage-eval-settings">
        <h3>Settings</h3>
        <div className="manage-grid">
          <ReadOnlyField label="Evaluation rounds" value={initial.rounds} requiredRole={permissions.field_min_role.number_of_eval_rounds} />
          <ReadOnlyField label="Leaderboard" value={initial.leaderboard} requiredRole={permissions.field_min_role.leaderboard} />
          <ReadOnlyField
            label="Judge feedback"
            value={initial.feedbackRequired ? 'Required' : 'Optional'}
            requiredRole={permissions.field_min_role.feedback_required}
          />
        </div>
      </section>
    );
  }

  const handleSave = async () => {
    const rounds = Number(values.rounds);
    if (!Number.isInteger(rounds) || rounds < 1) {
      setError('Evaluation rounds must be a whole number of at least 1.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const updated = await saveCompetitionFields(
        competition.id,
        { number_of_eval_rounds: rounds, leaderboard: values.leaderboard, feedback_required: values.feedbackRequired },
        permissions.field_min_role,
      );
      onSaved(updated);
      setToast('Evaluation settings saved');
    } catch (err) {
      setError(errorMessage(err, 'Failed to save evaluation settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="manage-section" data-tour="manage-eval-settings">
      <h3>Settings</h3>
      <div className="manage-grid">
        <div className="manage-field">
          <label htmlFor="eval-rounds" className="manage-field__label">Evaluation rounds</label>
          <input
            id="eval-rounds"
            type="number"
            min={1}
            className="input input--default input--md"
            value={values.rounds}
            onChange={(e) => setValues((prev) => ({ ...prev, rounds: e.target.value }))}
            disabled={saving}
          />
          <span className="manage-field__help">How many times each pair of submissions can be shown to judges.</span>
        </div>
        <div className="manage-field">
          <label htmlFor="eval-leaderboard" className="manage-field__label">Leaderboard</label>
          <select
            id="eval-leaderboard"
            className="input input--default input--md"
            value={values.leaderboard}
            onChange={(e) => setValues((prev) => ({ ...prev, leaderboard: e.target.value as Leaderboard }))}
            disabled={saving}
          >
            <option value="public">public</option>
            <option value="private">private</option>
            <option value="unlisted">unlisted</option>
            <option value="final">final</option>
          </select>
        </div>
        <div className="manage-field">
          <label htmlFor="eval-feedback" className="manage-field__label">Judge feedback</label>
          <select
            id="eval-feedback"
            className="input input--default input--md"
            value={values.feedbackRequired ? 'true' : 'false'}
            onChange={(e) => setValues((prev) => ({ ...prev, feedbackRequired: e.target.value === 'true' }))}
            disabled={saving}
          >
            <option value="false">Optional</option>
            <option value="true">Required</option>
          </select>
        </div>
      </div>
      {error && <p className="manage-error" role="alert">{error}</p>}
      <div className="manage-actions">
        <Button variant="secondary" disabled={!dirty || saving} onClick={() => { setValues(initial); setError(''); }}>
          Discard
        </Button>
        <Button variant="primary" loading={saving} disabled={!dirty || saving} onClick={handleSave}>
          Save settings
        </Button>
      </div>
      {toast && <Toast message={toast} duration={2500} onClose={() => setToast('')} />}
    </section>
  );
};

export default EvaluationSettingsForm;
